import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { Calendar, CalendarModule } from 'primeng/calendar';
import { Menu, MenuModule } from 'primeng/menu';
import { Subscription, pairwise, startWith, take } from 'rxjs';
import { TimeInputComponent, timeStringToDate, timeValidator } from 'src/app/shared/components/time-input/time-input.component';
import { IOrgSessionTimeFromToWhisperValue } from 'src/app/shared/models/organization/organization.model';

export interface IStartAtEndAtFG {
  startAt: FormControl<Date | null>;
  startAtTime: FormControl<string>;
  endAt: FormControl<Date | null>;
  endAtTime: FormControl<string>;
}

export const getStartAtEndAtFG = () => {
  return new FormGroup<IStartAtEndAtFG>({
    startAt: new FormControl<Date | null>(null),
    startAtTime: new FormControl<string>('', { nonNullable: true, validators: [ timeValidator.bind(this) ] }),
    endAt: new FormControl<Date | null>(null),
    endAtTime: new FormControl<string>('', { nonNullable: true, validators: [ timeValidator.bind(this) ] }),
  });
}

@Component({
  selector: 'app-start-at-end-at-input',
  standalone: true,
  imports: [
    ReactiveFormsModule, TranslateModule,
    CalendarModule, MenuModule,
    TimeInputComponent,
  ],
  templateUrl: './start-at-end-at-input.component.html',
  styleUrl: './start-at-end-at-input.component.scss'
})
export class StartAtEndAtInputComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input({required: true}) FG: FormGroup<IStartAtEndAtFG> | undefined;
  _whisperValues: IOrgSessionTimeFromToWhisperValue[] | undefined;
  get whisperValues() { return this._whisperValues; }
  @Input({required: true}) set whisperValues(val: IOrgSessionTimeFromToWhisperValue[] | undefined) { this._whisperValues = val; this.createWhisperValueMenuItems(); };

  @ViewChild('endCalendar') endCalendar: Calendar | undefined;

  whisperValueMenuItems: MenuItem[] = [];

  unhandledStartAtChange: Date | null = null;
  endAtValueToSetToStartAt: Date | null = null;

  subs: Subscription[] = [];

  ngOnInit(): void {
    this.watchDates();
  }

  ngAfterViewInit(): void {
    this.subs.push(
      this.endCalendar?.onBlur.subscribe(() => {
        if (this.endAtValueToSetToStartAt && this.FG) {
          this.updateStartAtByEndAt(this.FG, this.endAtValueToSetToStartAt);
          this.endAtValueToSetToStartAt = null;
        }
      }) ?? Subscription.EMPTY
    )
  }

  private watchDates() {
    if (!this.FG) return;
    const FG = this.FG;
    this.subs.push(
      FG.controls.startAt.valueChanges.pipe(
        startWith(FG.value.startAt), pairwise()
      ).subscribe(change => {
        const prevStartAt = this.unhandledStartAtChange ?? change[0];
        const nextStartAt = change[1];

        if (this.unhandledStartAtChange) this.unhandledStartAtChange = null;
        
        const endAt = FG.controls.endAt.value;

        if (!prevStartAt || !nextStartAt || !endAt) return;

        const oldStartAtToEndAtTimeDiff = Math.abs(endAt.getTime() - prevStartAt.getTime());
        const oldStartAtToEndAtDaysDiff = Math.floor(oldStartAtToEndAtTimeDiff / (1000 * 60 * 60 * 24));

        FG.controls.endAt.setValue(nextStartAt.addUTCDays(oldStartAtToEndAtDaysDiff), { emitEvent: false });
      }),

      FG.controls.endAt.valueChanges.pipe(
        startWith(FG.controls.endAt.value), pairwise()
      ).subscribe(change => {
        const prevEndAt = change[0];
        const nextEndAt = change[1];

        const startAt = FG.controls.startAt.value;
        if (!nextEndAt || !startAt) return;

        if (nextEndAt.getTime() < startAt.getTime()) {

          if (this.endCalendar?.focus) {
            // wait for the calendar to be unfocused (handled elsewhere)
            this.endAtValueToSetToStartAt = nextEndAt;
          } else {
            this.updateStartAtByEndAt(FG, nextEndAt);
          }
        }
      }),
      // ...this.sgUtils.initAndWatchDateTimes(FG.controls.startAt, FG.controls.endAt, FG.controls.startAtTime, FG.controls.endAtTime, FG.controls.startDate),
    );
  }
  updateStartAtByEndAt(FG: FormGroup<IStartAtEndAtFG>, value: Date) {
    this.endAtValueToSetToStartAt = null;
    FG.controls.startAt.setValue(value, { emitEvent: false });
    this.unhandledStartAtChange = value;
  };

  private createWhisperValueMenuItems() {
    this.whisperValueMenuItems = this.whisperValues?.map(value => {
      return {
        label: `${value.from} - ${value.to}`,
        value: this.createFromToKey(value.from, value.to),

        command: () => {
          if (!this.FG) return;
          this.FG.patchValue({
            startAtTime: value.from,
            endAtTime: value.to,
          })
        }
      }
    }) ?? [];
  }
  private createFromToKey(from: string, to: string) {
    return `${from}-${to}`;
  }

  onTimeInput(e: Event, whisperMenu: Menu) {
    if (this.whisperValues?.length) {
      whisperMenu.toggle(e);
  
      setTimeout(() => {
        (e.target as any)?.focus()
      })
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

}
